import { Component, effect, input, output } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';

interface DayInfo {
  day: number;
  class: string;
}

interface MonthData {
  name: string;
  monthIndex: number;
  days: DayInfo[];
}

@Component({
  selector: 'app-year-calendar',
  imports: [MatIconModule],
  templateUrl: './year-calendar.component.html',
  styleUrls: ['./year-calendar.component.css', '../styles/layout.css'],
})
export class YearCalendarComponent {
  events = input<any[]>();
  blocked = input<any[]>();
  initialYear = input<number>();

  yearChanged = output<number>();

  year: number = new Date().getFullYear();

  months: string[] = [
    'Enero',
    'Febrero',
    'Marzo',
    'Abril',
    'Mayo',
    'Junio',
    'Julio',
    'Agosto',
    'Septiembre',
    'Octubre',
    'Noviembre',
    'Diciembre',
  ];

  weekDays: string[] = ['L', 'M', 'X', 'J', 'V', 'S', 'D'];

  monthsData: MonthData[] = [];

  constructor() {
    effect(() => {
      const inputYear = this.initialYear();
      if (inputYear) {
        this.year = inputYear;
      }
      this.events();
      this.blocked();
      this.generateYearCalendar();
    });
  }

  ngOnInit() {
    this.yearChanged.emit(this.year);
  }

  generateYearCalendar() {
    this.monthsData = [];

    for (let monthIndex = 0; monthIndex < 12; monthIndex++) {
      const monthData: MonthData = {
        name: this.months[monthIndex],
        monthIndex: monthIndex,
        days: this.generateMonthDays(monthIndex),
      };
      this.monthsData.push(monthData);
    }
  }

  generateMonthDays(monthIndex: number): DayInfo[] {
    const days: DayInfo[] = [];

    // Get the first day of the month (0 = Sunday, 1 = Monday, etc.)
    const firstDayOfMonth = new Date(this.year, monthIndex, 1).getDay();
    // Adjust for Monday start
    const adjustedFirstDay = firstDayOfMonth === 0 ? 6 : firstDayOfMonth - 1;

    // Add empty placeholders for days before the 1st of the month
    for (let i = 0; i < adjustedFirstDay; i++) {
      days.push({ day: 0, class: 'empty-day' });
    }

    const lastDateOfMonth = new Date(this.year, monthIndex + 1, 0).getDate();

    // Get events and blocked days for this month
    const monthEvents = this.getEventsForMonth(monthIndex);
    const monthBlocked = this.getBlockedForMonth(monthIndex);

    // Days of the current month
    for (let i = 1; i <= lastDateOfMonth; i++) {
      let dayClass = 'current-month';

      // Check for vacation types
      if (monthEvents.approvedVacation.includes(i)) {
        dayClass = 'approved-vacation';
      } else if (monthEvents.approvedPersonalVacation.includes(i)) {
        dayClass = 'approved-personal-vacation';
      } else if (monthEvents.pendingVacation.includes(i)) {
        dayClass = 'pending-vacation';
      } else if (monthEvents.unavailability.has(i)) {
        dayClass = monthEvents.unavailability.get(i)!;
      } else if (monthEvents.generic.includes(i)) {
        dayClass = 'event';
      } else if (monthBlocked.includes(i)) {
        dayClass = 'blocked';
      }

      days.push({ day: i, class: dayClass });
    }

    return days;
  }

  getEventsForMonth(monthIndex: number): {
    generic: number[];
    approvedVacation: number[];
    approvedPersonalVacation: number[];
    pendingVacation: number[];
    unavailability: Map<number, string>;
  } {
    const result = {
      generic: [] as number[],
      approvedVacation: [] as number[],
      approvedPersonalVacation: [] as number[],
      pendingVacation: [] as number[],
      unavailability: new Map<number, string>(),
    };

    const currentEvents = this.events() ?? [];

    currentEvents.forEach((event) => {
      const eventDate = new Date(event.date);
      if (
        eventDate.getMonth() === monthIndex &&
        eventDate.getFullYear() === this.year
      ) {
        const day = eventDate.getDate();

        if (event.type === 'approved-vacation') {
          result.approvedVacation.push(day);
        } else if (event.type === 'approved-personal-vacation') {
          result.approvedPersonalVacation.push(day);
        } else if (event.type === 'pending-vacation') {
          result.pendingVacation.push(day);
        } else if (typeof event.type === 'string' && event.type.startsWith('unavailability-')) {
          result.unavailability.set(day, event.type);
        } else {
          result.generic.push(day);
        }
      }
    });

    return result;
  }

  getBlockedForMonth(monthIndex: number): number[] {
    const currentBlocked = this.blocked() ?? [];

    return currentBlocked
      .filter((block) => {
        const blockDate = new Date(block.date);
        return (
          blockDate.getMonth() === monthIndex &&
          blockDate.getFullYear() === this.year
        );
      })
      .map((block) => {
        const blockDate = new Date(block.date);
        return blockDate.getDate();
      });
  }

  changeYear(direction: number) {
    this.year += direction;
    this.generateYearCalendar();
    this.yearChanged.emit(this.year);
  }
}
